import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	inject,
	input,
	PLATFORM_ID,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { MarkdownModule } from 'ngx-markdown';
import type { SwiperContainer } from 'swiper/element/bundle';
import { register } from 'swiper/element/bundle';
import type { SwiperOptions } from 'swiper/types';

import type { ContentMargin } from '@valk-nx/components/ui-hero-item/src/lib/content-margin';
import { HeroItem } from '@valk-nx/components/ui-hero-item/src/lib/hero-item';
import { cn } from '@valk-nx/core/lib/core';
import { PreloadService } from '@valk-nx/core/lib/pipes/image-query/image-preload.service';
import { ImageQueryService } from '@valk-nx/core/lib/pipes/image-query/image-query.service';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';

import type { CarouselItem } from './carousel.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'vp-carousel',
	styleUrls: ['./carousel.scss'],
	templateUrl: './carousel.html',
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		SVG_ICON_IMPORTS,
		CommonModule,
		HeroItem,
		MarkdownModule,
		RouterModule,
	],
})
export class CarouselComponent implements OnInit, AfterViewInit {
	private readonly imageQuery = inject<ImageQueryService>(ImageQueryService);
	readonly platformId = inject(PLATFORM_ID);
	private readonly preloadService = inject(PreloadService);

	items = input.required<CarouselItem[]>();
	config = input<Partial<SwiperOptions>>({});
	size = input<'small' | 'large'>('large');
	useEagerLoading = input<boolean>(true);

	contentMargin = input<ContentMargin>('none');

	cn = cn;

	@ViewChild('swiper') swiperRef: ElementRef | undefined;

	swiper: SwiperContainer | null = null;

	swiperConfig: SwiperOptions = {};

	constructor() {
		register();
	}

	ngOnInit() {
		const preloadUrl = this.getPreloadUrl(
			this.items()[0].image?.src,
			this.size(),
		);
		this.preloadService.preloadImage(preloadUrl);
	}

	ngAfterViewInit() {
		this.swiperConfig = {
			/* v8 ignore start */
			loop: typeof window !== 'undefined' || this.items().length > 1,
			/* v8 ignore stop */
			preventClicks: false,
			spaceBetween: 0,
			speed: 500,
			...this.config(),
			keyboard: { enabled: true, onlyInViewport: true },
			navigation: false,
		};

		this.swiper = this.swiperRef?.nativeElement;

		if (this.swiper && isPlatformBrowser(this.platformId)) {
			Object.assign(this.swiper, this.swiperConfig);
			this.swiper.initialize();
		}
	}

	slideNext() {
		this.swiper?.swiper.slideNext(this.swiperConfig.speed);
	}

	slidePrev() {
		this.swiper?.swiper.slidePrev(this.swiperConfig.speed);
	}

	getPreloadUrl(src: string | undefined, size: 'small' | 'large'): string {
		if (!src) return '';

		const targetWidth = 800;
		const targetHeight = size === 'large' ? 640 : 320;
		return this.imageQuery.transform(src, {
			width: targetWidth,
			height: targetHeight,
		});
	}
}
